/**
 * Lot I2 (2c) — the split storage of a conversation's configuration and rollup, in ONE place.
 *
 * A conversation's cells, its per-language scripted options and its per-cell rollup results
 * live in their own items once they outgrow one row:
 *
 *   PK BRAND#<b>   · SK BPCELL#<p>#v<configVersion>#<cellKey>   one ConversationCell     ≤ 12 KB
 *   PK BRAND#<b>   · SK BPOPT#<p>#v<configVersion>#<language>   { turns: string[][] }    ≤ 64 KB
 *   PK PERSONA#<p> · SK RCELL#<launchId>#<passId>#<cellKey>     ConversationRollupCell   ≤ 16 KB
 *
 * No migration (C0): a legacy row keeps inline `cells` (and an inline `ROLLUP`); every reader
 * goes through the loaders below (`loadPersonaCellsWith`, `loadRollupCellsWith`), which read
 * both layouts. Cell identity is content-derived: only the config items carry `v<configVersion>`;
 * no result key does, so a cell's results and series survive every save.
 *
 * Pure helpers: no I/O, no AWS SDK, no Node-only APIs (the frontend bundles this package). The
 * loaders take their reads as callbacks; each runtime (API, Lambdas, MCP) supplies them and emits
 * its own EMF line when a loader throws `ConversationConfigIncompleteError`.
 */

import type { ConversationCell, ConversationPersonaRollup, ConversationPersonaRollupRow, ConversationPersonaRollupSplitRow, ConversationRollupCell, ConversationWindow } from '../types/conversation';
import { CONVERSATION_ENGINES, CONVERSATION_WINDOWS } from '../types/conversation';
import { conversationLaunchCost } from './brand-allowances';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * A rollup pass with MORE per-cell entries than this writes its `ROLLUP` split (`RCELL#` items +
 * `cellSetId`), else inline (today's shape). Decided per pass from the entries it writes (the
 * LAUNCH), never from the persona row's layout.
 */
export const ROLLUP_INLINE_MAX_CELLS = 12;

/**
 * Bytes (`itemSizeBytes`) above which a legacy inline persona row is converted to the split
 * layout even while its last launch is open (C17.4 R18): 300 cells each copying a 2 000-char
 * `startingPromptLocalized` pass 400 KB.
 */
export const INLINE_PERSONA_ROW_BUDGET = 350 * 1024;

/**
 * Days a superseded config version (`BPCELL#` / `BPOPT#` of a `configVersion` below the row's)
 * is kept before DynamoDB TTL reaps it (C17.4 R14: ≥ the engine DLQ's 14-day retention + the
 * TTL lag, so a redriven run still finds the version its launch claimed). A save never deletes
 * a version; it re-puts the old items with this TTL.
 */
export const CONVERSATION_CONFIG_RETIRE_TTL_DAYS = 15;

/** SQS limits the launch fan-out packs under (8d): one message, and one `SendMessageBatch` call. */
export const CONVERSATION_LAUNCH_MESSAGE_MAX_BYTES = 200 * 1024;
export const CONVERSATION_LAUNCH_BATCH_MAX_BYTES = 256 * 1024;
export const CONVERSATION_LAUNCH_BATCH_MAX_ENTRIES = 10;

// ---------------------------------------------------------------------------
// Key builders — the only place these SK formats are written
// ---------------------------------------------------------------------------

export type ConversationConfigItemKind = 'BPCELL' | 'BPOPT';

/** Every version of one persona's config items of `kind` (the delete cascade): `BPCELL#<p>#`. */
export function configPersonaPrefix(kind: ConversationConfigItemKind, personaId: string): string {
  return `${kind}#${personaId}#`;
}

/** One version's items of `kind` (the loader's Query): `BPCELL#<p>#v<v>#`. */
export function configVersionPrefix(kind: ConversationConfigItemKind, personaId: string, configVersion: string): string {
  return `${configPersonaPrefix(kind, personaId)}v${configVersion}#`;
}

/** `BPCELL#<p>#v<v>#<cellKey>` — one cell of one version. */
export function bpCellSk(personaId: string, configVersion: string, cellKey: string): string {
  return configVersionPrefix('BPCELL', personaId, configVersion) + cellKey;
}

/** `BPOPT#<p>#v<v>#<language>` — one language's `scripted_turn_options` of one version. */
export function bpOptSk(personaId: string, configVersion: string, language: string): string {
  return configVersionPrefix('BPOPT', personaId, configVersion) + language;
}

/**
 * `SK BETWEEN lo AND hi` = every version STRICTLY BELOW `configVersion`, never it (fixed-width
 * versions sort by time; `hi` sorts before any `…v<v>#<x>` key). The deferred retire of a save.
 */
export function configVersionsBelow(
  kind: ConversationConfigItemKind,
  personaId: string,
  configVersion: string,
): { lo: string; hi: string } {
  const base = configPersonaPrefix(kind, personaId);
  return { lo: `${base}v`, hi: `${base}v${configVersion}` };
}

/** `passId` of a rollup pass: its 13-digit, zero-padded epoch ms. */
export function rollupPassId(passMs: number): string {
  return String(passMs).padStart(13, '0');
}

/** `ROLLUP.cellSetId` = `<launchId>#<passId>`. */
export function rollupCellSetId(launchId: string, passId: string): string {
  return `${launchId}#${passId}`;
}

/** Every `RCELL#` item of one set (the loader's Query): `RCELL#<cellSetId>#`. */
export function rcellSetPrefix(cellSetId: string): string {
  return `RCELL#${cellSetId}#`;
}

/** Every `RCELL#` set of one launch, every pass (the D62 erase): `RCELL#<launchId>#`. */
export function rcellLaunchPrefix(launchId: string): string {
  return `RCELL#${launchId}#`;
}

/** `RCELL#<cellSetId>#<cellKey>`. */
export function rcellSk(cellSetId: string, cellKey: string): string {
  return rcellSetPrefix(cellSetId) + cellKey;
}

// ---------------------------------------------------------------------------
// Versions
// ---------------------------------------------------------------------------

const B32 = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * The next `configVersion`: `${13-digit ms, zero-padded}-${4 base32}`, fixed width, STRICTLY
 * greater than `prev` even when `nowMs` is behind it (a skewed clock): the ms part is
 * `max(nowMs, ms(prev) + 1)`.
 */
export function nextConfigVersion(prev: string | undefined, nowMs: number): string {
  const prevMs = prev ? Number(prev.slice(0, 13)) : -1;
  const ms = Math.max(nowMs, prevMs + 1);
  let suffix = '';
  for (let i = 0; i < 4; i++) suffix += B32[Math.floor(Math.random() * 32)];
  return `${String(ms).padStart(13, '0')}-${suffix}`;
}

// ---------------------------------------------------------------------------
// Runs per launch and the launched-cell set
// ---------------------------------------------------------------------------

/**
 * The ONE reader of a persona's runs per launch: `runsPerLaunch` on a split row (written by the
 * save, same write as `configVersion`), else `conversationLaunchCost(cells)` of the legacy inline
 * row. Never reads `BPCELL#`.
 */
export function personaRunsPerLaunch(row: { runsPerLaunch?: number; cells?: { runs: number }[] }): number {
  return row.runsPerLaunch !== undefined ? row.runsPerLaunch : conversationLaunchCost(row.cells ?? []);
}

/**
 * 8b — the ONE launched-cell set: the loaded cells (`loadPersonaCellsWith(row, …)`) whose
 * engine is a conversation engine, in stored order, nothing trimmed. Both producers (sweep,
 * `runNow`) derive the self-serve cost, the agency estimate and per-cell holds, `LNCH#.cellKeys`
 * and the message cells from this result only.
 */
export function launchCells(cells: ConversationCell[]): ConversationCell[] {
  return cells.filter((c) => (CONVERSATION_ENGINES as readonly string[]).includes(c.engine));
}

/**
 * The Contracts rule for `scripted_turn_options` (one language): length `maxTurns`, `[t-1]` = the
 * options of turn `t` in stored order (`byLanguage[language] ?? text`), `[]` = a generated turn;
 * `improvised` → every entry `[]`. The save precomputes it per cell language into `BPOPT#`; the
 * dispatcher builds the same array off an inline row. Never an LLM, never a fallback text.
 */
export function scriptedTurnOptionsFor(
  items: ReadonlyArray<{ turn: number; text: string; byLanguage?: Record<string, string> }> | undefined,
  mode: string | undefined,
  maxTurns: number,
  language: string,
): string[][] {
  const out: string[][] = [];
  for (let t = 1; t <= maxTurns; t++) {
    out.push(
      (mode ?? 'improvised') === 'improvised'
        ? []
        : (items ?? []).filter((q) => q.turn === t).map((q) => q.byLanguage?.[language] ?? q.text),
    );
  }
  return out;
}

// ---------------------------------------------------------------------------
// The launch counter `LNCH#<launchId> / COUNTER` (8d) — ONE builder for both producers
// ---------------------------------------------------------------------------

/** `LNCH#` lives 24 h (the reaper window, 8d); DynamoDB deletes expired rows with a lag. */
export const CONVERSATION_LAUNCH_COUNTER_TTL_SECONDS = 24 * 3600;

/** The value a producer / the launch DLQ consumer writes under `doneCells` for a cell that never ran. */
export const CONVERSATION_LAUNCH_DEAD_CELL = 'dead';

/** Counters-table key of a launch's counter. */
export function launchCounterKey(launchId: string): { PK: string; SK: string } {
  return { PK: `LNCH#${launchId}`, SK: 'COUNTER' };
}

/**
 * The `doneCells` map key of a cell: `c_<cellKey, # → _>`, exactly as the finalizer writes it
 * (a `#` cannot appear in a nested-map key token).
 */
export function launchDoneCellAttr(cellKey: string): string {
  return `c_${cellKey.replace(/#/g, '_')}`;
}

/**
 * The `LNCH#` row a producer (sweep, `runNow`) puts BEFORE its first chunk, with
 * `attribute_not_exists(PK)`: `cellKeys` = every cell it launches (holds taken, failed chunks
 * included), `completedCount: 0`, `deadCells: 0`, `doneCells: {}`, and NO `expected` — that is
 * SET only by the producer's conditional write after every chunk is enqueued (absent = not yet
 * closable for every reader). `configVersion` / `cellCount` only for a split persona.
 */
export function buildLaunchCounterItem(i: {
  launchId: string;
  tenantId: string;
  brandId: string;
  personaId: string;
  intentId: string;
  cellKeys: string[];
  configVersion?: string;
  cellCount?: number;
  nowMs: number;
}): Record<string, unknown> {
  return {
    ...launchCounterKey(i.launchId),
    completedCount: 0,
    deadCells: 0,
    doneCells: {},
    cellKeys: i.cellKeys,
    tenantId: i.tenantId,
    brandId: i.brandId,
    personaId: i.personaId,
    intentId: i.intentId,
    ...(i.configVersion !== undefined ? { configVersion: i.configVersion, cellCount: i.cellCount } : {}),
    createdAt: new Date(i.nowMs).toISOString(),
    ttl: Math.floor(i.nowMs / 1000) + CONVERSATION_LAUNCH_COUNTER_TTL_SECONDS,
  };
}

// ---------------------------------------------------------------------------
// Launch queue chunking (8d) — by BYTES, never by cell count
// ---------------------------------------------------------------------------

const utf8Encoder = new TextEncoder();

/** UTF-8 byte length of a JSON body (what SQS counts). */
export function jsonByteLength(value: unknown): number {
  return utf8Encoder.encode(JSON.stringify(value)).length;
}

/**
 * Split a launch's cells into `ConversationLaunchMessage` bodies: `base` (every field but
 * `cells`) plus as many cells as fit while the JSON stays ≤ `maxBytes`. Stored order kept,
 * nothing dropped; a single cell that alone exceeds the budget still gets its own message (the
 * cells carry only `ConversationLaunchCell` fields, so this cannot happen at the caps).
 */
export function chunkLaunchMessages<B extends object, C>(
  base: B,
  cells: readonly C[],
  maxBytes: number = CONVERSATION_LAUNCH_MESSAGE_MAX_BYTES,
): Array<B & { cells: C[] }> {
  const out: Array<B & { cells: C[] }> = [];
  const empty = jsonByteLength({ ...base, cells: [] });
  let current: C[] = [];
  let size = empty;
  for (const cell of cells) {
    const cellBytes = jsonByteLength(cell);
    // + 1 for the ',' separator after the first cell
    if (current.length > 0 && size + 1 + cellBytes > maxBytes) {
      out.push({ ...base, cells: current });
      current = [];
      size = empty;
    }
    size += cellBytes + (current.length > 0 ? 1 : 0);
    current.push(cell);
  }
  if (current.length > 0) out.push({ ...base, cells: current });
  return out;
}

/**
 * Pack message bodies into `SendMessageBatch` calls: ≤ `maxEntries` entries and ≤ `maxBytes`
 * of bodies per call (SQS limits are per message AND per call). Order kept.
 */
export function packLaunchBatches<M>(
  messages: readonly M[],
  maxBytes: number = CONVERSATION_LAUNCH_BATCH_MAX_BYTES,
  maxEntries: number = CONVERSATION_LAUNCH_BATCH_MAX_ENTRIES,
): M[][] {
  const out: M[][] = [];
  let current: M[] = [];
  let size = 0;
  for (const m of messages) {
    const bytes = jsonByteLength(m);
    if (current.length > 0 && (current.length >= maxEntries || size + bytes > maxBytes)) {
      out.push(current);
      current = [];
      size = 0;
    }
    current.push(m);
    size += bytes;
  }
  if (current.length > 0) out.push(current);
  return out;
}

// ---------------------------------------------------------------------------
// Loaders — the inline-or-split rule and the count check
// ---------------------------------------------------------------------------

/**
 * A split set read short of its pointer's count, twice (the pointer moved, then the set was
 * still incomplete). Never answered with a partial list or `[]`: the runtime emits its EMF line
 * (`ConversationConfigIncomplete`; rollup: 503 `ROLLUP_UNAVAILABLE`) and fails the operation.
 */
export class ConversationConfigIncompleteError extends Error {
  constructor(
    readonly prefix: string,
    readonly expected: number,
    readonly found: number,
  ) {
    super(`Split set ${prefix} incomplete: ${found} of ${expected} items`);
    this.name = 'ConversationConfigIncompleteError';
  }
}

/** The persona row fields the cell loader reads. */
export interface PersonaConfigPointer {
  personaId: string;
  cells?: ConversationCell[];
  configVersion?: string;
  cellCount?: number;
}

/**
 * A persona's cells, either layout:
 * - `row.cells !== undefined` → `row.cells` (legacy inline), no read;
 * - else `queryItems(configVersionPrefix('BPCELL', p, configVersion))` (a `queryAll`,
 *   `ConsistentRead`, every page); a count ≠ `row.cellCount` → `rereadRow()` once (the pointer
 *   moved) and retry with the row it returns; still short → `ConversationConfigIncompleteError`.
 * Split cells come back in SK (`cellKey`) order.
 */
export async function loadPersonaCellsWith<R extends PersonaConfigPointer>(
  row: R,
  io: { queryItems(skPrefix: string): Promise<ConversationCell[]>; rereadRow(): Promise<R | undefined> },
): Promise<ConversationCell[]> {
  let current: R | undefined = row;
  for (let attempt = 0; attempt < 2; attempt++) {
    if (!current) break;
    if (current.cells !== undefined) return current.cells;
    const prefix = configVersionPrefix('BPCELL', current.personaId, current.configVersion ?? '');
    const items = await io.queryItems(prefix);
    if (items.length === current.cellCount) return items;
    if (attempt === 1) throw new ConversationConfigIncompleteError(prefix, current.cellCount ?? 0, items.length);
    current = await io.rereadRow();
  }
  throw new ConversationConfigIncompleteError(configPersonaPrefix('BPCELL', row.personaId), row.cellCount ?? 0, 0);
}

function isSplitRollup(row: ConversationPersonaRollupRow): row is ConversationPersonaRollupSplitRow {
  return (row as ConversationPersonaRollupSplitRow).cellSetId !== undefined;
}

/**
 * A stored `ROLLUP` row in its assembled (API) shape. Decided by the ROLLUP row, never by the
 * persona row: no `cellSetId` → returned as is; `cellSetId` → `queryItems(rcellSetPrefix(id))`,
 * the same count check and one `rereadRollup()` as `loadPersonaCellsWith`, then
 * `assembleRollup`.
 */
export async function loadRollupCellsWith(
  row: ConversationPersonaRollupRow,
  io: {
    queryItems(skPrefix: string): Promise<ConversationRollupCell[]>;
    rereadRollup(): Promise<ConversationPersonaRollupRow | undefined>;
  },
): Promise<ConversationPersonaRollup> {
  let current: ConversationPersonaRollupRow | undefined = row;
  for (let attempt = 0; attempt < 2; attempt++) {
    if (!current) break;
    if (!isSplitRollup(current)) return current;
    const prefix = rcellSetPrefix(current.cellSetId);
    const items = await io.queryItems(prefix);
    if (items.length === current.cellCount) return assembleRollup(current, items);
    if (attempt === 1) throw new ConversationConfigIncompleteError(prefix, current.cellCount, items.length);
    current = await io.rereadRollup();
  }
  throw new ConversationConfigIncompleteError(`RCELL#`, 0, 0);
}

/**
 * Rebuild the inline shape from a split row and its COMPLETE `RCELL#` set: each window's
 * `byCell` in its stored `pos` order, `trend` keyed by cellKey. Inverse of `splitRollup`.
 */
export function assembleRollup(
  row: ConversationPersonaRollupSplitRow,
  cells: ConversationRollupCell[],
): ConversationPersonaRollup {
  const { cellSetId: _set, cellCount: _count, windows, ...rest } = row;
  const outWindows = {} as ConversationPersonaRollup['windows'];
  for (const w of CONVERSATION_WINDOWS) {
    const entries: { pos: number; entry: ConversationPersonaRollup['windows'][ConversationWindow]['byCell'][number] }[] = [];
    for (const c of cells) {
      const e = c.windows[w];
      if (!e) continue;
      const { pos, ...values } = e;
      entries.push({ pos, entry: { cellKey: c.cellKey, engine: c.engine, language: c.language, zone: c.zone, ...values } });
    }
    entries.sort((a, b) => a.pos - b.pos);
    outWindows[w] = { ...windows[w], byCell: entries.map((x) => x.entry) };
  }
  const trend: ConversationPersonaRollup['trend'] = {};
  for (const c of cells) if (c.trend !== undefined) trend[c.cellKey] = c.trend;
  return { ...rest, windows: outWindows, trend };
}

/**
 * The split form of an assembled rollup: the `ROLLUP` row (windows without `byCell`, no `trend`,
 * `cellSetId`, `cellCount`) and one `RCELL#` body per cell of the union of every window's `byCell`
 * and `trend`. `assembleRollup(row, cells)` deep-equals the input.
 */
export function splitRollup(
  rollup: ConversationPersonaRollup,
  cellSetId: string,
): { row: ConversationPersonaRollupSplitRow; cells: ConversationRollupCell[] } {
  const { windows, trend, ...rest } = rollup;
  const byKey = new Map<string, ConversationRollupCell>();
  const cellOf = (cellKey: string, engine: string, language: string, zone: string): ConversationRollupCell => {
    let c = byKey.get(cellKey);
    if (!c) {
      c = { cellKey, engine, language, zone, windows: {} };
      byKey.set(cellKey, c);
    }
    return c;
  };
  const rowWindows = {} as ConversationPersonaRollupSplitRow['windows'];
  for (const w of CONVERSATION_WINDOWS) {
    const { byCell, ...slice } = windows[w];
    rowWindows[w] = slice;
    byCell.forEach(({ cellKey, engine, language, zone, ...values }, pos) => {
      cellOf(cellKey, engine, language, zone).windows[w] = { ...values, pos };
    });
  }
  for (const [cellKey, points] of Object.entries(trend)) {
    const [engine = '', language = '', zone = ''] = cellKey.split('#');
    cellOf(cellKey, engine, language, zone).trend = points;
  }
  const cells = [...byKey.values()];
  return { row: { ...rest, windows: rowWindows, cellSetId, cellCount: cells.length }, cells };
}

// ---------------------------------------------------------------------------
// Item size
// ---------------------------------------------------------------------------

const utf8 = new TextEncoder();

function numberSize(n: number | bigint): number {
  // 1 byte per two significant digits + 1, plus 1 for base-100 pair alignment (an upper bound).
  const mantissa = String(n).toLowerCase().replace('-', '').split('e')[0];
  const digits = mantissa.replace('.', '').replace(/^0+/, '').replace(/0+$/, '').length;
  return Math.ceil((Math.max(digits, 1) + 1) / 2) + 1;
}

function valueSize(v: unknown): number {
  if (typeof v === 'string') return utf8.encode(v).length;
  if (typeof v === 'number' || typeof v === 'bigint') return numberSize(v);
  if (typeof v === 'boolean' || v === null) return 1;
  if (v instanceof Uint8Array) return v.byteLength;
  if (v instanceof Set) {
    let total = 0;
    for (const e of v) total += valueSize(e);
    return total;
  }
  if (Array.isArray(v)) {
    let total = 3;
    for (const e of v) if (e !== undefined) total += 1 + valueSize(e);
    return total;
  }
  let total = 3;
  for (const [k, e] of Object.entries(v as Record<string, unknown>)) {
    if (e !== undefined) total += utf8.encode(k).length + 1 + valueSize(e);
  }
  return total;
}

/**
 * DynamoDB size of `item` as `marshall(item, { removeUndefinedValues: true })` would store it:
 * attribute names + values (UTF-8 strings, numbers ≈ 1 byte per 2 digits + 1, 1-byte booleans /
 * nulls, 3 bytes + 1 per element for lists and maps, `undefined` dropped and lists compacted).
 * An upper bound to within a byte per number; used by the R18 persona-row budget and the size test.
 */
export function itemSizeBytes(item: Record<string, unknown>): number {
  let total = 0;
  for (const [k, v] of Object.entries(item)) {
    if (v !== undefined) total += utf8.encode(k).length + valueSize(v);
  }
  return total;
}
